import { ListChecks } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Card, CardHeader } from '@/components/ui/Card'
import { cn } from '@/utils/cn'
import type { CoverageRow, CoverageStatus } from '../types'
import { subjectDotStyle } from '../week'

const STATUS: Record<CoverageStatus, { label: string; tone: 'warning' | 'success' | 'info'; bar: string }> = {
  under: { label: 'Under', tone: 'warning', bar: 'bg-warning-500' },
  ok: { label: 'On target', tone: 'success', bar: 'bg-success-500' },
  over: { label: 'Over', tone: 'info', bar: 'bg-info-500' },
}

/** Curriculum coverage: scheduled lessons per subject against the weekly target. */
export function CoveragePanel({ rows }: { rows: CoverageRow[] }) {
  const { t } = useTranslation('timetable')
  const scheduled = rows.reduce((n, r) => n + r.scheduled, 0)
  const target = rows.reduce((n, r) => n + (r.target ?? 0), 0)
  const onTarget = rows.filter((r) => r.status === 'ok').length
  return (
    <Card className="print:hidden">
      <CardHeader
        title={t('Curriculum coverage')}
        description={t('{{scheduled}} of {{target}} weekly periods scheduled', { scheduled, target })}
        icon={<ListChecks />}
      />
      {rows.length === 0 ? (
        <EmptyState
          compact
          icon={<ListChecks />}
          title={t('No curriculum yet')}
          description={t('Add subjects to this class to plan its timetable.')}
        />
      ) : (
        <>
          <p className="px-5 pt-3 text-xs text-muted">
            {t('{{count}} of {{total}} subjects on target', { count: onTarget, total: rows.length })}
          </p>
          <ul className="divide-y divide-line">
            {rows.map((r) => {
              const status = STATUS[r.status]
              const pct = r.target
                ? Math.min(100, Math.round((r.scheduled / r.target) * 100))
                : r.scheduled
                  ? 100
                  : 0
              return (
                <li key={r.subject.id} className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={cn('size-2.5 shrink-0 rounded-full', !r.subject.color && 'bg-brand-400')}
                      style={subjectDotStyle(r.subject.color)}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                      {r.subject.name}
                    </span>
                    <span className="text-xs text-muted tabular">
                      {r.target !== null ? `${r.scheduled}/${r.target}` : r.scheduled}
                    </span>
                    <Badge tone={status.tone} size="sm">
                      {t(status.label)}
                    </Badge>
                  </div>
                  <div
                    className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-sunken"
                    role="progressbar"
                    aria-label={t('{{subject}} coverage', { subject: r.subject.name })}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={pct}
                  >
                    <div className={cn('h-full rounded-full', status.bar)} style={{ width: `${pct}%` }} />
                  </div>
                  {r.isOptional ? <p className="mt-1 text-2xs text-muted">{t('Elective')}</p> : null}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </Card>
  )
}
